import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import {
  Alert,
  Box,
  Button,
  Logo,
  Spinner,
  TextField,
  Typography,
  portalChrome,
} from '@glplus/glpweb-dsl';
import {
  answerChallenge,
  hasSession,
  safeNextPath,
  startSignIn,
  type LoginStage,
} from '../lib/authFlow';

const CHROME = portalChrome.vendor;
const ORANGE = '#F67A1A';
const ORANGE_DARK = '#DF6514';
const APPLY_URL = `${(import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ?? 'https://www.grocerylistplus.com'}/vendors/apply`;

/**
 * /login — the portal-owned VENDOR-pool sign-in (pool-per-actor cutover,
 * vendor build Phase 0, 2026-09-17). Rendered OUTSIDE usePortalGuard (the
 * guard redirects here, so guarding it would loop). Stages: credentials →
 * (first sign-in: new password) → (optional TOTP) → redirect to ?next.
 *
 * Accounts are provisioned by an application (/vendors/apply on the public
 * site) or by a team invite; there is no self-signup. Password reset is
 * "contact support" for now (same posture as the admin portal v1).
 *
 * Mirrors glp-admin-portal/src/routes/Login.tsx on vendor chrome. A Figma
 * frame for this screen is owed — the admin frames (16/16b/16c) are the
 * design basis.
 */

type Feedback =
  | { severity: 'error' | 'warning'; title?: string; body: string }
  | null;

const FIELD_LABEL_SX = { fontWeight: 700, fontSize: '12px', color: '#6b7280', mb: 0.5 } as const;
const PRIMARY_BTN_SX = {
  backgroundColor: ORANGE,
  '&:hover': { backgroundColor: ORANGE_DARK },
  width: '100%',
} as const;

export default function Login() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));

  const [checking, setChecking] = useState(true);
  const [stage, setStage] = useState<LoginStage>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busy, setBusy] = useState(false);

  // A live session skips the form entirely.
  useEffect(() => {
    void hasSession().then((live) => {
      if (live) window.location.assign(next);
      else setChecking(false);
    });
  }, [next]);

  const finish = () => window.location.assign(next);

  const run = async (fn: () => Promise<LoginStage>) => {
    setBusy(true);
    setFeedback(null);
    try {
      const out = await fn();
      if (out.kind === 'done') finish();
      else setStage(out);
    } catch (err) {
      setFeedback(mapError(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <Shell>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
          <Spinner aria-label={t('common.checkingAccess')} />
        </Box>
      </Shell>
    );
  }

  return (
    <Shell>
      <LoginCard>
        {feedback && (
          <Alert severity={feedback.severity} title={feedback.title} sx={{ mb: 2 }}>
            {feedback.body}
          </Alert>
        )}
        {stage.kind === 'credentials' && (
          <CredentialsStage
            email={email}
            onEmail={setEmail}
            busy={busy}
            onSubmit={(password) => void run(() => startSignIn(email.trim().toLowerCase(), password))}
          />
        )}
        {stage.kind === 'newPassword' && (
          <NewPasswordStage busy={busy} onSubmit={(pw) => void run(() => answerChallenge(pw, email))} />
        )}
        {stage.kind === 'totpSetup' && (
          <TotpSetupStage
            setupUri={stage.setupUri}
            secret={stage.secret}
            busy={busy}
            onSubmit={(code) => void run(() => answerChallenge(code, email))}
          />
        )}
        {stage.kind === 'totpCode' && (
          <TotpCodeStage
            email={email}
            busy={busy}
            onSubmit={(code) => void run(() => answerChallenge(code, email))}
            onStartOver={() => {
              setStage({ kind: 'credentials' });
              setFeedback(null);
            }}
          />
        )}
      </LoginCard>
    </Shell>
  );
}

// ─── Stages ──────────────────────────────────────────────────────────────────

function CredentialsStage({
  email,
  onEmail,
  busy,
  onSubmit,
}: {
  email: string;
  onEmail: (v: string) => void;
  busy: boolean;
  onSubmit: (password: string) => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const ready = /.+@.+\..+/.test(email.trim()) && password.length > 0;
  return (
    <Stage
      title={t('login.title')}
      subtitle={t('login.subtitle')}
      onEnter={() => ready && !busy && onSubmit(password)}
    >
      <Field label={t('login.email')}>
        <Box data-testid="login-email">
          <TextField
            ariaLabel={t('login.email')}
            size="small"
            type="email"
            placeholder="you@yourstore.com"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            sx={{ width: '100%' }}
          />
        </Box>
      </Field>
      <Field label={t('login.password')}>
        <Box data-testid="login-password">
          <PasswordField ariaLabel={t('login.password')} value={password} onChange={setPassword} />
        </Box>
      </Field>
      <Box data-testid="login-submit">
        <Button
          ariaLabel={t('login.signIn')}
          buttonText={busy ? t('login.signingIn') : t('login.signIn')}
          variant="contained"
          disabled={busy || !ready}
          sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
          onClick={() => onSubmit(password)}
        />
      </Box>
      <Typography sx={{ mt: 2, fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
        {t('login.noAccount')}{' '}
        <Box component="a" href={APPLY_URL} sx={{ color: '#A85200', fontWeight: 700 }}>
          {t('login.applyLink')}
        </Box>
      </Typography>
      <Typography sx={{ mt: 1, fontSize: '12px', color: '#98a2b3', textAlign: 'center' }}>
        {t('login.trouble')}
      </Typography>
    </Stage>
  );
}

const PASSWORD_REQUIREMENTS: Array<[string, (pw: string) => boolean]> = [
  ['login.req.length', (pw) => pw.length >= 12],
  ['login.req.case', (pw) => /[a-z]/.test(pw) && /[A-Z]/.test(pw)],
  ['login.req.number', (pw) => /\d/.test(pw)],
  ['login.req.symbol', (pw) => /[^A-Za-z0-9]/.test(pw)],
];

function NewPasswordStage({ busy, onSubmit }: { busy: boolean; onSubmit: (pw: string) => void }) {
  const { t } = useTranslation();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const allMet = PASSWORD_REQUIREMENTS.every(([, ok]) => ok(pw));
  const ready = allMet && pw === pw2;
  return (
    <Stage
      title={t('login.newPassword.title')}
      subtitle={t('login.newPassword.subtitle')}
      onEnter={() => ready && !busy && onSubmit(pw)}
    >
      <Field label={t('login.newPassword.label')}>
        <PasswordField ariaLabel={t('login.newPassword.label')} value={pw} onChange={setPw} />
      </Field>
      <Field label={t('login.newPassword.confirm')}>
        <PasswordField ariaLabel={t('login.newPassword.confirm')} value={pw2} onChange={setPw2} />
      </Field>
      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {PASSWORD_REQUIREMENTS.map(([key, ok]) => (
          <Typography key={key} sx={{ fontSize: '12px', color: ok(pw) ? '#1F7026' : '#6b7280' }}>
            ✓ {t(key)}
          </Typography>
        ))}
        {pw2.length > 0 && pw !== pw2 && (
          <Typography sx={{ fontSize: '12px', color: '#b42318' }}>{t('login.newPassword.mismatch')}</Typography>
        )}
      </Box>
      <Button
        ariaLabel={t('common.continue')}
        buttonText={busy ? t('common.saving') : t('common.continue')}
        variant="contained"
        disabled={busy || !ready}
        sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
        onClick={() => onSubmit(pw)}
      />
    </Stage>
  );
}

function TotpSetupStage({
  setupUri,
  secret,
  busy,
  onSubmit,
}: {
  setupUri: string;
  secret: string;
  busy: boolean;
  onSubmit: (code: string) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    void QRCode.toDataURL(setupUri, { margin: 1, width: 224 }).then(setQr);
  }, [setupUri]);
  const ready = /^\d{6}$/.test(code.replace(/\s/g, ''));
  const groupedSecret = secret.replace(/(.{4})/g, '$1 ').trim();
  return (
    <Stage
      title={t('login.totp.setupTitle')}
      subtitle={t('login.totp.setupSubtitle')}
      onEnter={() => ready && !busy && onSubmit(code.replace(/\s/g, ''))}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 1 }}>
        <Box
          sx={{
            width: 112,
            height: 112,
            flexShrink: 0,
            border: '1px solid #d0d5dd',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {qr ? (
            <img src={qr} alt={t('login.totp.qrAlt')} width={110} height={110} />
          ) : (
            <Spinner aria-label={t('common.loading')} size="small" />
          )}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>{t('login.totp.scan')}</Typography>
          <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.5 }}>{t('login.totp.cantScan')}</Typography>
        </Box>
      </Box>
      <Field label={t('login.totp.setupKey')}>
        <Typography sx={{ fontSize: '13px', color: '#1f2937', wordBreak: 'break-all' }}>{groupedSecret}</Typography>
      </Field>
      <Field label={t('login.totp.code')}>
        <TextField
          ariaLabel={t('login.totp.code')}
          size="small"
          placeholder="123 456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          sx={{ width: '100%' }}
        />
      </Field>
      <Button
        ariaLabel={t('login.totp.verify')}
        buttonText={busy ? t('login.totp.verifying') : t('login.totp.verify')}
        variant="contained"
        disabled={busy || !ready}
        sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
        onClick={() => onSubmit(code.replace(/\s/g, ''))}
      />
    </Stage>
  );
}

function TotpCodeStage({
  email,
  busy,
  onSubmit,
  onStartOver,
}: {
  email: string;
  busy: boolean;
  onSubmit: (code: string) => void;
  onStartOver: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const ready = /^\d{6}$/.test(code.replace(/\s/g, ''));
  return (
    <Stage
      title={t('login.totp.title')}
      subtitle={email || t('login.totp.subtitle')}
      onEnter={() => ready && !busy && onSubmit(code.replace(/\s/g, ''))}
    >
      <Field label={t('login.totp.code')}>
        <TextField
          ariaLabel={t('login.totp.code')}
          size="small"
          placeholder="123 456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          sx={{ width: '100%' }}
        />
      </Field>
      <Button
        ariaLabel={t('login.totp.verify')}
        buttonText={busy ? t('login.totp.verifying') : t('login.totp.verify')}
        variant="contained"
        disabled={busy || !ready}
        sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
        onClick={() => onSubmit(code.replace(/\s/g, ''))}
      />
      <Button
        ariaLabel={t('login.startOver')}
        buttonText={t('login.startOver')}
        variant="text"
        size="small"
        sx={{ mt: 1, alignSelf: 'center' }}
        onClick={onStartOver}
      />
    </Stage>
  );
}

// ─── Chrome / layout ─────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Box
        component="header"
        sx={{
          background: { xs: CHROME.headerBgMobile, md: CHROME.headerBg },
          color: '#fff',
          minHeight: 64,
          px: { xs: '16px', sm: '32px', lg: '40px' },
          py: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Logo product="vendor" height={48} dataTestId="login-vendor-logo" />
      </Box>
      {children}
    </Box>
  );
}

function LoginCard({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pt: { xs: 6, md: 12 }, pb: 8 }}>
      <Box
        sx={{
          width: 420,
          maxWidth: '100%',
          backgroundColor: '#fff',
          borderRadius: '10px',
          boxShadow: '0 8px 24px -4px rgba(16,24,40,0.10)',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Stage({
  title,
  subtitle,
  onEnter,
  children,
}: {
  title: string;
  subtitle: string;
  onEnter: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="form"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        onEnter();
      }}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#1f2937' }}>{title}</Typography>
      <Typography sx={{ fontSize: '13px', color: '#6b7280', mt: 0.5 }}>{subtitle}</Typography>
      {children}
      {/* Hidden submit so Enter works inside the form */}
      <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
    </Box>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}

/** Password input with the DSL's built-in show/hide (eye) toggle. */
function PasswordField({
  ariaLabel,
  value,
  onChange,
}: {
  ariaLabel: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      ariaLabel={ariaLabel}
      size="small"
      type={visible ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      endAdornment="visibility"
      handleIconClick={() => setVisible((v) => !v)}
      sx={{ width: '100%' }}
    />
  );
}

// ─── Error mapping ───────────────────────────────────────────────────────────

function mapError(err: unknown, t: (k: string) => string): Feedback {
  const name = (err as { name?: string })?.name ?? '';
  const message = (err as { message?: string })?.message ?? '';

  if (name === 'UnsupportedStepError') {
    return { severity: 'error', body: t('login.errors.unsupported') };
  }
  if (/temporary password has expired/i.test(message)) {
    return { severity: 'warning', title: t('login.errors.inviteExpiredTitle'), body: t('login.errors.inviteExpired') };
  }
  if (name === 'NotAuthorizedException' || name === 'UserNotFoundException') {
    return { severity: 'error', title: t('login.errors.badCredentialsTitle'), body: t('login.errors.badCredentials') };
  }
  if (name === 'CodeMismatchException' || name === 'ExpiredCodeException') {
    return { severity: 'error', body: t('login.errors.badCode') };
  }
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
    return { severity: 'error', body: t('login.errors.tooMany') };
  }
  return { severity: 'error', body: message || t('login.errors.generic') };
}
